import { Box, Container, Typography, Grid, Button, Stack,
         Chip, Dialog, DialogContent, IconButton, LinearProgress,
         Divider, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { Fade } from "@mui/material";
import HandshakeIcon      from "@mui/icons-material/Handshake";
import EmailIcon          from "@mui/icons-material/Email";
import TagIcon            from "@mui/icons-material/Tag";
import ArrowForwardIcon   from "@mui/icons-material/ArrowForward";
import GroupsIcon         from "@mui/icons-material/Groups";
import StarIcon           from "@mui/icons-material/Star";
import PublicIcon         from "@mui/icons-material/Public";
import LightbulbIcon      from "@mui/icons-material/Lightbulb";
import CloseIcon          from "@mui/icons-material/Close";
import PhoneAndroidIcon   from "@mui/icons-material/PhoneAndroid";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FavoriteIcon       from "@mui/icons-material/Favorite";
import LocationOnIcon     from "@mui/icons-material/LocationOn";
import PeopleIcon         from "@mui/icons-material/People";
import ImageIcon          from "@mui/icons-material/Image";
import InfoOutlinedIcon   from "@mui/icons-material/InfoOutlined";
import { useLocation } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://www.africabuildtoshelter.com/api";

const MEDIA_ROOT = "https://www.africabuildtoshelter.com";
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${MEDIA_ROOT}${url.startsWith("/") ? "" : "/"}${url}`;
};

// ── Helper: project has donation data ────────────────────────────────────────
// Returns true only if goal or raised is a real non-zero number
const hasFunding = (project) => {
  const goal   = Number(project?.goal_ugx   ?? project?.goal   ?? 0);
  const raised = Number(project?.raised_ugx ?? project?.raised ?? 0);
  return goal > 0 || raised > 0;
};

// ─── Global palette ───────────────────────────────────────────────────────────
const SDG_BAR = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2",
  "#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E",
  "#3F7E44","#0A97D9","#56C02B","#00689D","#19486A",
];

const WHITE      = "#ffffff";
const OFF_WHITE  = "#f7f4f1";
const LIGHT_BG   = "#faf8f6";
const BROWN      = "#5C3311";
const BROWN_DARK = "#3e220b";
const GOLD       = "#DDA63A";
const GREEN      = "#4C9F38";
const TEXT_DARK  = "#1a0e05";
const TEXT_MID   = "#4a3020";
const TEXT_LIGHT = "#8a6a50";

// ─── Volunteer roles ──────────────────────────────────────────────────────────
const roles = [
  { label: "Projects",                          img: "role-projects.jpg"    },
  { label: "Communications & Public Relations", img: "role-comms.jpg"       },
  { label: "Partnership & Donor Engagement",    img: "role-partnership.jpg" },
  { label: "Content Creation & Design",         img: "role-design.jpg"      },
  { label: "Research & Documentation",          img: "role-research.jpg"    },
  { label: "Finance",                           img: "role-finance.jpg"     },
];

// ─── Stats ────────────────────────────────────────────────────────────────────
const stats = [
  { value: "6",    label: "Volunteer Roles",      icon: <GroupsIcon /> },
  { value: "22+",  label: "African Countries",    icon: <PublicIcon /> },
  { value: "2025", label: "Year Founded",         icon: <StarIcon /> },
  { value: "SDGs", label: "Goal-Aligned Mission", icon: <LightbulbIcon /> },
];

// ─── Donation data ────────────────────────────────────────────────────────────
const MOBILE_MONEY = [
  {
    provider: "MTN Mobile Money",
    number: "+256 761 070 710",
    name: "Build To Shelter Africa",
    label: "MTN",
    color: "#FCC30B",
    textColor: "#000",
    instructions: "Dial *165# → Send Money → Enter number → Enter amount → Confirm",
  },
  {
    provider: "Airtel Money",
    number: "+256 752 123 456",
    name: "Build To Shelter Africa",
    label: "AIRTEL",
    color: "#E5243B",
    textColor: "#fff",
    instructions: "Dial *185# → Send Money → Enter number → Enter amount → Confirm",
  },
];

const BANK = {
  bankName:      "Stanbic Bank Uganda",
  accountName:   "Build To Shelter Africa",
  accountNumber: "9030 0124 5678 9",
  branchCode:    "010",
  swiftCode:     "SBICUGKX",
  branch:        "Kampala Main Branch",
};

// ─── API hook ─────────────────────────────────────────────────────────────────
function useApi(endpoint) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [endpoint]);

  return { data, loading, error };
}

// ─── Image placeholder ────────────────────────────────────────────────────────
function ImgPlaceholder({ height = 210 }) {
  return (
    <Box sx={{
      height, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      bgcolor: alpha(BROWN, 0.06), gap: 1,
    }}>
      <ImageIcon sx={{ fontSize: 40, color: alpha(BROWN, 0.2) }} />
      <Typography sx={{ fontSize: "11px", color: alpha(BROWN, 0.35), fontFamily: "'DM Sans', sans-serif" }}>
        No image
      </Typography>
    </Box>
  );
}

// ─── SDG Image Wheel ──────────────────────────────────────────────────────────
function SDGImageWheel() {
  const images = roles.map((r) => `/${r.img}`);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((p) => (p + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <Box sx={{ position: "relative", width: 300, height: 300 }}>
      <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle, ${alpha(BROWN,0.25)} 0%, ${alpha(GOLD,0.1)} 50%, transparent 75%)`, filter: "blur(24px)" }} />
      <Box sx={{ position: "absolute", inset: -8, borderRadius: "50%", background: `conic-gradient(${SDG_BAR.map((c,i) => `${c} ${(i/SDG_BAR.length)*100}% ${((i+1)/SDG_BAR.length)*100}%`).join(", ")})`, opacity: 0.85, "@keyframes slowSpin": { from:{ transform:"rotate(0deg)" }, to:{ transform:"rotate(360deg)" } }, animation: "slowSpin 30s linear infinite" }} />
      <Box sx={{ position: "absolute", inset: 8, borderRadius: "50%", bgcolor: WHITE }} />
      <Box sx={{ position: "absolute", inset: 8, borderRadius: "50%", overflow: "hidden", zIndex: 2 }}>
        {images.map((src, i) => (
          <Fade key={src} in={i === idx} timeout={1000} mountOnEnter unmountOnExit>
            <Box component="img" src={src} alt="" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", "@keyframes float": { "0%,100%":{ transform:"translateY(0)" }, "50%":{ transform:"translateY(-8px)" } }, animation: "float 6s ease-in-out infinite" }} />
          </Fade>
        ))}
      </Box>
      <Box sx={{ position: "absolute", bottom: -28, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 0.8, zIndex: 3 }}>
        {images.map((_, i) => (
          <Box key={i} onClick={() => setIdx(i)} sx={{ width: i===idx ? 16 : 6, height: 6, borderRadius: "3px", bgcolor: i===idx ? BROWN : alpha(BROWN,0.25), cursor: "pointer", transition: "all 0.3s" }} />
        ))}
      </Box>
    </Box>
  );
}

// ─── Volunteer Apply Modal ────────────────────────────────────────────────────
function VolunteerModal({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: WHITE, color: TEXT_DARK, borderRadius: 3, overflow: "hidden", border: `1px solid ${alpha(BROWN,0.12)}`, boxShadow: `0 24px 64px ${alpha(BROWN,0.2)}` } }}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BROWN}, ${GOLD}, #4C9F38)` }} />
      <DialogContent sx={{ p: { xs: 3, md: 4 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", fontWeight: 700, color: TEXT_DARK, lineHeight: 1.2, mb: 0.5 }}>
              Send Your Application
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.85rem", color: TEXT_LIGHT }}>
              Join our Pan-African volunteer team
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: TEXT_LIGHT, "&:hover": { color: TEXT_DARK } }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ bgcolor: alpha(BROWN, 0.05), border: `1px solid ${alpha(BROWN, 0.15)}`, borderLeft: `4px solid ${BROWN}`, borderRadius: 2, p: 2.5, mb: 3 }}>
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: BROWN, letterSpacing: "2px", textTransform: "uppercase", fontWeight: 700, mb: 1 }}>
            How to Apply
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.92rem", color: TEXT_MID, lineHeight: 1.8 }}>
            Email your <Box component="span" sx={{ fontWeight: 700, color: TEXT_DARK }}>CV</Box> and a short{" "}
            <Box component="span" sx={{ fontWeight: 700, color: TEXT_DARK }}>motivation note</Box> telling us which role interests you and why you want to build Africa's future.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: alpha(BROWN, 0.04), border: `1px solid ${alpha(BROWN, 0.18)}`, borderRadius: 2, px: 2.5, py: 2, mb: 3 }}>
          <EmailIcon sx={{ color: BROWN, fontSize: 20, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "13px", color: BROWN, fontWeight: 700 }}>
            africabuildtoshelter@gmail.com
          </Typography>
        </Box>
        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: TEXT_LIGHT, letterSpacing: "1.5px", textTransform: "uppercase", mb: 1 }}>
            Also tag us on social media:
          </Typography>
          <Chip label="#BuildToShelterAfrica" icon={<TagIcon style={{ fontSize: 14 }} />}
            sx={{ bgcolor: alpha(GOLD, 0.15), color: BROWN, border: `1px solid ${alpha(GOLD, 0.5)}`, fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: "11px", "& .MuiChip-icon": { color: BROWN } }} />
        </Box>
        <Divider sx={{ borderColor: alpha(BROWN, 0.1), mb: 3 }} />
        <Button fullWidth variant="contained" size="large"
          href="mailto:africabuildtoshelter@gmail.com?subject=Volunteer%20Application%20-%20Build%20To%20Shelter%20Africa&body=Hello%2C%0A%0AI%20would%20like%20to%20apply%20to%20volunteer%20with%20Build%20To%20Shelter%20Africa.%0A%0ARole%20I%27m%20interested%20in%3A%0A%0AMotivation%3A%0A%0APlease%20find%20my%20CV%20attached.%0A%0AThank%20you."
          startIcon={<EmailIcon />} endIcon={<ArrowForwardIcon />}
          sx={{ bgcolor: BROWN, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "15px", py: 1.5, borderRadius: 2, "&:hover": { bgcolor: BROWN_DARK }, boxShadow: `0 6px 24px ${alpha(BROWN, 0.35)}` }}>
          Open Email to Apply
        </Button>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_LIGHT, textAlign: "center", mt: 1.5 }}>
          This will open your email app with a pre-filled template
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

// ─── Project Detail Modal ─────────────────────────────────────────────────────
function ProjectModal({ project, open, onClose }) {
  const [tab, setTab] = useState("mobile");
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => { if (open) setImgErr(false); }, [open]);

  if (!project) return null;

  const raised   = Number(project.raised_ugx ?? project.raised ?? 0);
  const goal     = Number(project.goal_ugx   ?? project.goal   ?? 0);
  const pct      = goal > 0 ? Math.min(Math.round((raised / goal) * 100), 100) : 0;
  const imgSrc   = resolveImg(project.image ?? project.cover_image);
  const showDonate = hasFunding(project);  // ← key flag

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: WHITE, color: TEXT_DARK, borderRadius: 3, overflow: "hidden", border: `1px solid ${alpha(BROWN,0.12)}`, boxShadow: `0 24px 64px ${alpha(BROWN,0.2)}` } }}>

      {/* Hero image */}
      <Box sx={{ position: "relative", height: { xs: 180, md: 240 }, overflow: "hidden", bgcolor: alpha(BROWN, 0.1) }}>
        {imgSrc && !imgErr ? (
          <Box component="img" src={imgSrc} alt={project.title}
            onError={() => setImgErr(true)}
            sx={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.55)" }} />
        ) : (
          <ImgPlaceholder height="100%" />
        )}
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)" }} />
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${BROWN}, ${GOLD})` }} />
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 12, bgcolor: alpha("#fff",0.9), color: TEXT_DARK, "&:hover":{ bgcolor: WHITE } }}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ position: "absolute", bottom: 20, left: 24, right: 60 }}>
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.4rem", md: "1.9rem" }, fontWeight: 800, lineHeight: 1.1, color: WHITE }}>
            {project.title}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.6}>
            <LocationOnIcon sx={{ fontSize: 13, color: GOLD }} />
            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.75)", fontFamily: "'DM Sans', sans-serif" }}>
              {project.location}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 2.5, md: 4 }, bgcolor: WHITE }}>

        {/* ── Funding progress — only when goal/raised is set ── */}
        {showDonate && (
          <Box sx={{ mb: 3.5 }}>
            <Stack direction="row" justifyContent="space-between" mb={0.8}>
              <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "11px", color: BROWN, fontWeight: 700 }}>
                UGX {raised.toLocaleString()} raised
              </Typography>
              <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "11px", color: TEXT_LIGHT }}>
                Goal: UGX {goal.toLocaleString()}
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={pct}
              sx={{ height: 7, borderRadius: 4, bgcolor: alpha(BROWN,0.1), "& .MuiLinearProgress-bar": { background: `linear-gradient(90deg, ${BROWN}, ${GOLD})`, borderRadius: 4 } }} />
            <Stack direction="row" justifyContent="space-between" mt={0.6}>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: BROWN, fontWeight: 700 }}>{pct}% funded</Typography>
              {(project.beneficiaries > 0) && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PeopleIcon sx={{ fontSize: 13, color: TEXT_LIGHT }} />
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_LIGHT }}>
                    {project.beneficiaries} beneficiaries
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        )}

        {/* ── About ── */}
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: BROWN, mb: 1, fontSize: "1.1rem" }}>
          About This Project
        </Typography>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.92rem", color: TEXT_MID, lineHeight: 1.85, mb: 3 }}>
          {project.about ?? project.details ?? project.description}
        </Typography>

        {/* ── Impact ── */}
        {(project.impact || (project.impact_homes_built > 0)) && (
          <>
            <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: TEXT_LIGHT, letterSpacing: "2px", textTransform: "uppercase", mb: 1.5 }}>
              Expected Impact
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, mb: 3.5 }}>
              {(project.impact ?? [
                project.impact_homes_built    && `${project.impact_homes_built} homes built`,
                project.impact_families_housed && `${project.impact_families_housed} families housed`,
                project.impact_youth_trained   && `${project.impact_youth_trained} youth trained`,
                project.impact_cost_reduction  && project.impact_cost_reduction,
              ].filter(Boolean)).map((item) => (
                <Box key={item} sx={{ p: 1.5, bgcolor: alpha(BROWN,0.05), border: `1px solid ${alpha(BROWN,0.12)}`, borderRadius: 1.5 }}>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DARK, lineHeight: 1.4, fontWeight: 600 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* ── Donation section — only when goal/raised is set ── */}
        {showDonate ? (
          <>
            <Divider sx={{ borderColor: alpha(BROWN,0.1), mb: 3 }} />

            <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
              <FavoriteIcon sx={{ fontSize: 18, color: BROWN }} />
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.2rem", fontWeight: 700, color: TEXT_DARK }}>
                How to Donate
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} mb={3}>
              {[
                { key: "mobile", label: "Mobile Money",  icon: <PhoneAndroidIcon sx={{ fontSize: 16 }} /> },
                { key: "bank",   label: "Bank Transfer", icon: <AccountBalanceIcon sx={{ fontSize: 16 }} /> },
              ].map((t) => (
                <Button key={t.key} onClick={() => setTab(t.key)} startIcon={t.icon} size="small"
                  variant={tab === t.key ? "contained" : "outlined"}
                  sx={tab === t.key
                    ? { bgcolor: BROWN, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "12px", borderRadius: 1.5, "&:hover":{ bgcolor: BROWN_DARK } }
                    : { borderColor: alpha(BROWN,0.35), color: TEXT_MID, fontFamily: "'DM Sans', sans-serif", fontSize: "12px", borderRadius: 1.5, "&:hover":{ borderColor: BROWN, bgcolor: alpha(BROWN,0.05) } }
                  }>
                  {t.label}
                </Button>
              ))}
            </Stack>

            {tab === "mobile" && (
              <Stack spacing={2}>
                {MOBILE_MONEY.map((m) => (
                  <Box key={m.provider} sx={{ p: 2.5, border: `1px solid ${alpha(BROWN,0.12)}`, borderRadius: 2, bgcolor: OFF_WHITE }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Box sx={{ bgcolor: m.color, color: m.textColor, px: 1.5, py: 0.4, borderRadius: 1, fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: "11px" }}>
                        {m.label}
                      </Box>
                      <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "13px", fontWeight: 700, color: TEXT_DARK }}>
                        {m.number}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_LIGHT, mb: 1 }}>
                      Account name: <Box component="span" sx={{ color: TEXT_DARK, fontWeight: 700 }}>{m.name}</Box>
                    </Typography>
                    <Box sx={{ bgcolor: alpha(BROWN,0.05), border: `1px solid ${alpha(BROWN,0.12)}`, borderLeft: `3px solid ${BROWN}`, borderRadius: 1.5, p: 1.5 }}>
                      <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: TEXT_MID, lineHeight: 1.7 }}>
                        {m.instructions}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_LIGHT, textAlign: "center" }}>
                  After sending, email confirmation to{" "}
                  <Box component="a" href="mailto:africabuildtoshelter@gmail.com" sx={{ color: BROWN, textDecoration: "none", fontWeight: 700 }}>africabuildtoshelter@gmail.com</Box>
                  {" "}with <Box component="span" sx={{ color: BROWN, fontWeight: 700 }}>"{project.title}"</Box> as reference.
                </Typography>
              </Stack>
            )}

            {tab === "bank" && (
              <Box sx={{ border: `1px solid ${alpha(BROWN,0.12)}`, borderRadius: 2, overflow: "hidden" }}>
                {[
                  ["Bank",           BANK.bankName],
                  ["Account Name",   BANK.accountName],
                  ["Account Number", BANK.accountNumber],
                  ["Branch Code",    BANK.branchCode],
                  ["SWIFT / BIC",    BANK.swiftCode],
                  ["Branch",         BANK.branch],
                  ["Reference",      project.title],
                ].map(([label, value], i) => (
                  <Stack key={label} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ px: 2.5, py: 1.6, bgcolor: i % 2 === 0 ? OFF_WHITE : WHITE, borderBottom: i < 6 ? `1px solid ${alpha(BROWN,0.07)}` : "none" }}>
                    <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: TEXT_LIGHT, letterSpacing: "1.5px", textTransform: "uppercase" }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "13px", color: label === "Reference" ? BROWN : TEXT_DARK }}>
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </>
        ) : (
          // ── No donation info — friendly note ──
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, bgcolor: alpha(BROWN, 0.04), border: `1px solid ${alpha(BROWN, 0.12)}`, borderLeft: `4px solid ${GOLD}`, borderRadius: 2, p: 2, mt: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: GOLD, mt: 0.2, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.85rem", color: TEXT_MID, lineHeight: 1.7 }}>
              This project is not currently accepting donations.
              To get involved, reach out at{" "}
              <Box component="a" href="mailto:africabuildtoshelter@gmail.com"
                sx={{ color: BROWN, fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                africabuildtoshelter@gmail.com
              </Box>
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Project Card ─────────────────────────────────────────────────────────────
function ProjectCard({ project, onClick }) {
  const [imgErr, setImgErr] = useState(false);
  const raised     = Number(project.raised_ugx ?? project.raised ?? 0);
  const goal       = Number(project.goal_ugx   ?? project.goal   ?? 0);
  const pct        = goal > 0 ? Math.min(Math.round((raised / goal) * 100), 100) : 0;
  const imgSrc     = resolveImg(project.image ?? project.cover_image);
  const showDonate = hasFunding(project);  // ← key flag

  return (
    <Box onClick={onClick} sx={{
      cursor: "pointer", borderRadius: 3, overflow: "hidden",
      border: `1px solid ${alpha(BROWN,0.12)}`, bgcolor: WHITE,
      boxShadow: `0 2px 16px ${alpha(BROWN,0.07)}`,
      transition: "all 0.3s ease",
      "&:hover": { transform: "translateY(-6px)", border: `1px solid ${alpha(BROWN,0.35)}`, boxShadow: `0 16px 40px ${alpha(BROWN,0.15)}` },
      "&:hover .card-img": { transform: "scale(1.06)" },
    }}>
      {/* Image */}
      <Box sx={{ height: 210, overflow: "hidden", position: "relative", bgcolor: alpha(BROWN, 0.08) }}>
        {imgSrc && !imgErr ? (
          <Box className="card-img" component="img" src={imgSrc} alt={project.title}
            onError={() => setImgErr(true)}
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.5s ease" }} />
        ) : (
          <ImgPlaceholder height={210} />
        )}
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(92,51,17,0.4) 0%, transparent 55%)" }} />
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${BROWN}, ${GOLD})` }} />
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" mb={0.7}>
          <LocationOnIcon sx={{ fontSize: 12, color: GOLD }} />
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_LIGHT }}>
            {project.location}
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.3, mb: 1.2, color: TEXT_DARK }}>
          {project.title}
        </Typography>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.82rem", color: TEXT_MID, lineHeight: 1.75,
          // give more lines when there's no funding bar below
          mb: showDonate ? 2.5 : 2,
          display: "-webkit-box", WebkitLineClamp: showDonate ? 3 : 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {project.description ?? project.summary}
        </Typography>

        {/* ── Funding bar + stats — only when goal/raised is set ── */}
        {showDonate && (
          <>
            <LinearProgress variant="determinate" value={pct}
              sx={{ height: 5, borderRadius: 3, mb: 0.8, bgcolor: alpha(BROWN,0.1), "& .MuiLinearProgress-bar": { background: `linear-gradient(90deg, ${BROWN}, ${GOLD})`, borderRadius: 3 } }} />
            <Stack direction="row" justifyContent="space-between" mb={2.5}>
              <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: BROWN, fontWeight: 700 }}>{pct}% funded</Typography>
              {(project.beneficiaries > 0) && (
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", color: TEXT_LIGHT }}>
                  {project.beneficiaries} beneficiaries
                </Typography>
              )}
            </Stack>
            <Button fullWidth variant="contained" endIcon={<FavoriteIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ bgcolor: BROWN, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase", py: 1.15, borderRadius: 2, boxShadow: `0 4px 16px ${alpha(BROWN,0.3)}`, "&:hover": { bgcolor: BROWN_DARK, boxShadow: `0 6px 20px ${alpha(BROWN,0.4)}` } }}>
              Donate to This Project
            </Button>
          </>
        )}

        {/* ── No funding — subtle "Learn More" CTA ── */}
        {!showDonate && (
          <Button fullWidth variant="outlined" endIcon={<ArrowForwardIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ borderColor: alpha(BROWN, 0.35), color: BROWN, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase", py: 1.15, borderRadius: 2, "&:hover": { borderColor: BROWN, bgcolor: alpha(BROWN, 0.05) } }}>
            Learn More
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ─── Volunteer Card ───────────────────────────────────────────────────────────
function VolunteerCard({ vol }) {
  const [imgErr, setImgErr] = useState(false);
  const photo = resolveImg(vol.photo_url ?? vol.photo ?? vol.img);

  return (
    <Box sx={{
      bgcolor: WHITE, borderRadius: 3, overflow: "hidden",
      border: `1px solid ${alpha(BROWN, 0.12)}`,
      boxShadow: `0 4px 24px ${alpha(BROWN, 0.09)}`,
      transition: "all 0.3s ease",
      "&:hover": { transform: "translateY(-6px)", boxShadow: `0 16px 40px ${alpha(BROWN, 0.18)}`, border: `1px solid ${alpha(GOLD, 0.5)}` },
    }}>
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BROWN}, ${GOLD})` }} />
      <Box sx={{ position: "relative", height: 280, overflow: "hidden", bgcolor: alpha(BROWN, 0.05) }}>
        {photo && !imgErr ? (
          <Box component="img" src={photo} alt={vol.name}
            onError={() => setImgErr(true)}
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }} />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(BROWN, 0.08) }}>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "4rem", fontWeight: 700, color: alpha(BROWN, 0.3) }}>
              {vol.name?.[0] ?? "?"}
            </Typography>
          </Box>
        )}
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(92,51,17,0.55) 0%, transparent 55%)" }} />
        <Box sx={{ position: "absolute", top: 14, right: 14, width: 44, height: 44, borderRadius: "50%", background: `linear-gradient(135deg, ${GOLD}, #f5d07a)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 16px ${alpha(GOLD, 0.7)}`, border: `2px solid ${WHITE}` }}>
          <StarIcon sx={{ fontSize: 22, color: BROWN }} />
        </Box>
        <Box sx={{ position: "absolute", bottom: 14, left: 14 }}>
          <Chip label={vol.month_label ?? vol.month} size="small"
            sx={{ bgcolor: alpha(GOLD, 0.92), color: BROWN_DARK, fontFamily: "'Space Mono', monospace", fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px", height: 22 }} />
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: "1.15rem", color: TEXT_DARK, mb: 0.4, lineHeight: 1.2 }}>
          {vol.name}
        </Typography>
        <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "9.5px", color: BROWN, letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 700, mb: 1.5 }}>
          {vol.role_type_display ?? vol.role ?? vol.department}
        </Typography>
        <Divider sx={{ borderColor: alpha(BROWN, 0.1), mb: 1.5 }} />
        {vol.quote && (
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.82rem", color: TEXT_MID, lineHeight: 1.75, fontStyle: "italic" }}>
            "{vol.quote}"
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Loading spinner ──────────────────────────────────────────────────────────
function SectionLoader() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress sx={{ color: BROWN }} />
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function VolunteerPage() {
  const [selectedProject, setSelectedProject]       = useState(null);
  const [volunteerModalOpen, setVolunteerModalOpen] = useState(false);
  const { hash } = useLocation();

  const { data: volunteerData, loading: volLoading }  = useApi("/volunteers/");
  const { data: projectData,   loading: projLoading } = useApi("/projects/?status=active");

  const featured      = volunteerData?.featured ?? null;
  const restVols      = volunteerData?.volunteers ?? [];
  const allVolunteers = [...(featured ? [featured] : []), ...restVols];

  const projects = projectData?.projects ?? (Array.isArray(projectData) ? projectData : []);

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [hash]);

  const handleProjectClick = async (project) => {
    try {
      const res  = await fetch(`${API_BASE}/projects/${project.id}/`);
      const data = await res.json();
      setSelectedProject(data);
    } catch {
      setSelectedProject(project);
    }
  };

  return (
    <Box sx={{ bgcolor: WHITE, minHeight: "100vh", color: TEXT_DARK, pt: "72px" }}>

      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <Box sx={{ position: "relative", minHeight: { xs: "60vh", md: "68vh" }, display: "flex", alignItems: "center", overflow: "hidden" }}>
        <Box component="img" src="/volunter.jpg" alt=""
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.95 }} />
        <Box sx={{ position: "absolute", inset: 0, background: `linear-gradient(to right, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.52) 60%, rgba(255,255,255,0.1) 100%)` }} />
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "5px", display: "flex" }}>
          {SDG_BAR.map((c,i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
        </Box>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, py: { xs: 7, md: 11 } }}>
          <Chip label="Join Our Mission" size="small"
            sx={{ bgcolor: alpha(BROWN,0.1), color: BROWN, border: `1px solid ${alpha(BROWN,0.3)}`, fontFamily: "'Space Mono', monospace", fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", mb: 3, fontWeight: 700 }} />
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4rem" }, fontWeight: 900, lineHeight: 1.1, maxWidth: 700, mb: 3, color: TEXT_DARK }}>
            Volunteer &amp;{" "}<Box component="span" sx={{ color: BROWN }}>Partner</Box>{" "}With Us
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: { xs: "1rem", md: "1.15rem" }, color: TEXT_MID, maxWidth: 560, lineHeight: 1.8, mb: 4 }}>
            Build To Shelter Africa is a youth-led Pan-African non-profit building sustainable shelters
            and empowering communities. We seek passionate volunteers and visionary partners.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button variant="contained" size="large" onClick={() => setVolunteerModalOpen(true)} endIcon={<ArrowForwardIcon />}
              sx={{ bgcolor: BROWN, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "14px", px: 3.5, py: 1.3, borderRadius: 2, "&:hover":{ bgcolor: BROWN_DARK }, boxShadow: `0 6px 24px ${alpha(BROWN,0.35)}` }}>
              Volunteer Now
            </Button>
            <Button variant="outlined" size="large" href="mailto:africabuildtoshelter@gmail.com" endIcon={<HandshakeIcon />}
              sx={{ borderColor: BROWN, color: BROWN, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "14px", px: 3.5, py: 1.3, borderRadius: 2, "&:hover":{ borderColor: BROWN_DARK, bgcolor: alpha(BROWN,0.07) } }}>
              Become a Partner
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ══ VOLUNTEER OF THE MONTH ════════════════════════════════════════════ */}
    <Box sx={{ bgcolor: OFF_WHITE, borderTop: `1px solid ${alpha(BROWN, 0.08)}` }}>
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Stack alignItems="center" mb={1.5}>
          <Box sx={{ width: 40, height: 3, background: `linear-gradient(90deg,#E5243B,${GOLD},${GREEN})`, borderRadius: 2, mb: 2 }} />
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "11px", color: BROWN, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700 }}>
            Recognition
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.8rem", md: "2.6rem" }, fontWeight: 800, textAlign: "center", color: TEXT_DARK, mb: 1 }}>
          Volunteers of the Month
        </Typography>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_LIGHT, fontSize: "1rem", textAlign: "center", mb: 7, maxWidth: 500, mx: "auto" }}>
          Celebrating our outstanding Pan-African volunteers who go above and beyond every month.
        </Typography>

        {volLoading ? (
          <SectionLoader />
        ) : allVolunteers.length === 0 ? (
          <Typography sx={{ textAlign: "center", color: TEXT_LIGHT, fontFamily: "'DM Sans', sans-serif", py: 4 }}>
            No volunteers featured yet.
          </Typography>
        ) : (
          <>
            {/* ── Mobile: horizontal scroll carousel ── */}
            <Box sx={{ display: { xs: "block", md: "none" }, position: "relative" }}>
              {/* Left arrow */}
              <Box
                onClick={() => {
                  const el = document.getElementById("volunteers-scroll-track");
                  if (el) el.scrollBy({ left: -280, behavior: "smooth" });
                }}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "absolute", left: -8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 10, width: 36, height: 36, borderRadius: "50%",
                  bgcolor: "#fff", boxShadow: `0 2px 12px ${alpha(BROWN, 0.18)}`,
                  border: `1px solid ${alpha(BROWN, 0.1)}`, cursor: "pointer",
                  "&:active": { boxShadow: `0 1px 4px ${alpha(BROWN, 0.12)}` },
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8L10 4" stroke={BROWN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>

              {/* Right arrow */}
              <Box
                onClick={() => {
                  const el = document.getElementById("volunteers-scroll-track");
                  if (el) el.scrollBy({ left: 280, behavior: "smooth" });
                }}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "absolute", right: -8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 10, width: 36, height: 36, borderRadius: "50%",
                  bgcolor: "#fff", boxShadow: `0 2px 12px ${alpha(BROWN, 0.18)}`,
                  border: `1px solid ${alpha(BROWN, 0.1)}`, cursor: "pointer",
                  "&:active": { boxShadow: `0 1px 4px ${alpha(BROWN, 0.12)}` },
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke={BROWN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>

              {/* Scroll track */}
              <Box
                id="volunteers-scroll-track"
                sx={{
                  display: "flex", flexDirection: "row", gap: 2,
                  overflowX: "auto", overflowY: "hidden",
                  pb: 2, px: "4px",
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {allVolunteers.map((vol) => (
                  <Box
                    key={vol.id}
                    sx={{ flex: "0 0 78vw", maxWidth: 300, scrollSnapAlign: "start" }}
                  >
                    <VolunteerCard vol={vol} />
                  </Box>
                ))}
              </Box>

              {/* Dot indicators */}
              <Stack direction="row" justifyContent="center" spacing={0.75} mt={2}>
                {allVolunteers.map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: i === 0 ? 18 : 6, height: 6, borderRadius: 3,
                      bgcolor: i === 0 ? GOLD : alpha(BROWN, 0.2),
                      transition: "width 0.3s",
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* ── Desktop: original grid ── */}
            <Grid container spacing={4} justifyContent="center" sx={{ display: { xs: "none", md: "flex" } }}>
              {allVolunteers.map((vol) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={vol.id}>
                  <VolunteerCard vol={vol} />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Stats — unchanged */}
        <Box sx={{ mt: 8, pt: 6, borderTop: `1px solid ${alpha(BROWN, 0.1)}` }}>
          <Grid container spacing={3} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Stack alignItems="center" spacing={0.8}>
                  <Box sx={{ color: GOLD, "& svg": { fontSize: 26 } }}>{s.icon}</Box>
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "2rem", fontWeight: 900, color: TEXT_DARK, lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", color: TEXT_LIGHT, textTransform: "uppercase", letterSpacing: "1.5px", textAlign: "center" }}>
                    {s.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>

          {/* ══ VOLUNTEER ROLES ═══════════════════════════════════════════════════ */}
          <Box id="volunteer-roles" sx={{ bgcolor: WHITE }}>
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
                <Box sx={{ width: 40, height: 3, background: `linear-gradient(90deg,#E5243B,${GOLD},${GREEN})`, borderRadius: 2 }} />
                <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "11px", color: BROWN, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700 }}>Open Positions</Typography>
              </Stack>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 700, mb: 1, color: TEXT_DARK }}>
                Volunteer Roles Available
              </Typography>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_LIGHT, fontSize: "1rem", mb: 6, maxWidth: 520 }}>
                All positions are remote-friendly and Pan-African in scope.
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 3 }}>
                {roles.map((role) => (
                  <Box key={role.label} sx={{ "&:hover .role-img":{ transform: "scale(1.05)" } }}>
                    <Box sx={{ borderRadius: 2, overflow: "hidden", mb: 2, height: 220, position: "relative", boxShadow: `0 4px 20px ${alpha(BROWN,0.12)}` }}>
                      <Box className="role-img" component="img" src={`/${role.img}`} alt={role.label}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.45s ease" }} />
                      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${BROWN}, ${GOLD})` }} />
                    </Box>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "0.95rem", color: TEXT_DARK, textAlign: "center", lineHeight: 1.4 }}>{role.label}</Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_LIGHT, textAlign: "center", mt: 0.5, letterSpacing: "1px", textTransform: "uppercase" }}>
                      Volunteer · Remote · Pan-African
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>

      {/* ══ HOW TO APPLY ══════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: OFF_WHITE, borderTop: `1px solid ${alpha(BROWN,0.08)}`, borderBottom: `1px solid ${alpha(BROWN,0.08)}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
                <Box sx={{ width: 40, height: 3, background: `linear-gradient(90deg,#E5243B,${GOLD},${GREEN})`, borderRadius: 2 }} />
                <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "11px", color: BROWN, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700 }}>Apply Today</Typography>
              </Stack>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 700, mb: 2, color: TEXT_DARK }}>How to Apply</Typography>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_MID, fontSize: "1rem", lineHeight: 1.8, mb: 4 }}>
                If you share our values of sustainability, innovation and Pan-Africanism, we want to hear from you.
              </Typography>
              {[
                { num: "01", title: "Send your application", filled: true,
                  body: <>Email your CV and a short motivation note to{" "}
                    <Box component="a" href="mailto:africabuildtoshelter@gmail.com" sx={{ color: BROWN, textDecoration: "none", fontWeight: 700, "&:hover":{ textDecoration:"underline" } }}>africabuildtoshelter@gmail.com</Box></> },
                { num: "02", title: "Spread the word", filled: false,
                  body: <>Share on social media using{" "}
                    <Chip label="#BuildToShelterAfrica" size="small" icon={<TagIcon style={{ fontSize: 14 }} />}
                      sx={{ bgcolor: alpha(BROWN,0.08), color: BROWN, border: `1px solid ${alpha(BROWN,0.25)}`, fontFamily: "'Space Mono', monospace", fontSize: "10px", height: 22, "& .MuiChip-icon":{ color: BROWN } }} /></> },
              ].map((step) => (
                <Stack key={step.num} direction="row" spacing={2.5} alignItems="flex-start" mb={3.5}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, bgcolor: step.filled ? BROWN : WHITE, border: step.filled ? "none" : `2px solid ${alpha(BROWN,0.4)}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: "13px", color: step.filled ? "#fff" : BROWN, boxShadow: step.filled ? `0 4px 14px ${alpha(BROWN,0.35)}` : "none" }}>
                    {step.num}
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, mb: 0.5, color: TEXT_DARK }}>{step.title}</Typography>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", color: TEXT_MID, lineHeight: 1.7 }}>{step.body}</Typography>
                  </Box>
                </Stack>
              ))}
              <Button variant="contained" size="large" onClick={() => setVolunteerModalOpen(true)}
                startIcon={<EmailIcon />} endIcon={<ArrowForwardIcon />}
                sx={{ bgcolor: BROWN, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "14px", px: 3.5, py: 1.3, borderRadius: 2, "&:hover":{ bgcolor: BROWN_DARK }, boxShadow: `0 6px 24px ${alpha(BROWN,0.35)}` }}>
                Apply to Volunteer
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
              <SDGImageWheel />
            </Grid>
          </Grid>
        </Container>
      </Box>

              {/* ══ ONGOING PROJECTS ══════════════════════════════════════════════════ */}
        <Box id="ongoing-projects" sx={{ bgcolor: LIGHT_BG, borderTop: `1px solid ${alpha(BROWN, 0.08)}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
              <Box sx={{ width: 40, height: 3, background: `linear-gradient(90deg,#E5243B,${GOLD},${GREEN})`, borderRadius: 2 }} />
              <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "11px", color: BROWN, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700 }}>
                Make a Difference
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} mb={7} spacing={2}>
              <Box>
                <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 700, mb: 1, color: TEXT_DARK }}>
                  Ongoing Projects
                </Typography>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_LIGHT, fontSize: "1rem", maxWidth: 480 }}>
                  Tap any project to view full details and donate via mobile money or bank transfer.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
                <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: TEXT_LIGHT, letterSpacing: "1.5px" }}>
                  {projectData?.active_count ?? projects.length} ACTIVE PROJECTS
                </Typography>
              </Stack>
            </Stack>

            {projLoading ? (
              <SectionLoader />
            ) : projects.length === 0 ? (
              <Typography sx={{ textAlign: "center", color: TEXT_LIGHT, fontFamily: "'DM Sans', sans-serif", py: 4 }}>
                No active projects at the moment.
              </Typography>
            ) : (
              <>
                {/* ── Mobile: horizontal scroll carousel ── */}
                <Box sx={{ display: { xs: "block", md: "none" }, position: "relative" }}>
                  {/* Left arrow — only shows after user has scrolled right */}
                  <Box
                    onClick={() => {
                      const el = document.getElementById("projects-scroll-track");
                      if (el) el.scrollBy({ left: -280, behavior: "smooth" });
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "absolute",
                      left: -8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 10,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      boxShadow: `0 2px 12px ${alpha(BROWN, 0.18)}`,
                      border: `1px solid ${alpha(BROWN, 0.1)}`,
                      cursor: "pointer",
                      transition: "box-shadow 0.2s",
                      "&:active": { boxShadow: `0 1px 4px ${alpha(BROWN, 0.12)}` },
                    }}
                  >
                    {/* Left chevron */}
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10 12L6 8L10 4" stroke={BROWN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>

                  {/* Right arrow */}
                  <Box
                    onClick={() => {
                      const el = document.getElementById("projects-scroll-track");
                      if (el) el.scrollBy({ left: 280, behavior: "smooth" });
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "absolute",
                      right: -8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 10,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      boxShadow: `0 2px 12px ${alpha(BROWN, 0.18)}`,
                      border: `1px solid ${alpha(BROWN, 0.1)}`,
                      cursor: "pointer",
                      transition: "box-shadow 0.2s",
                      "&:active": { boxShadow: `0 1px 4px ${alpha(BROWN, 0.12)}` },
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4L10 8L6 12" stroke={BROWN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>

                  {/* Scroll track */}
                  <Box
                    id="projects-scroll-track"
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      overflowX: "auto",
                      overflowY: "hidden",
                      pb: 2,               // room for scrollbar / shadow
                      px: "4px",           // so card shadows aren't clipped
                      scrollSnapType: "x mandatory",
                      WebkitOverflowScrolling: "touch",
                      // hide scrollbar visually
                      "&::-webkit-scrollbar": { display: "none" },
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                    }}
                  >
                    {projects.map((p) => (
                      <Box
                        key={p.id}
                        sx={{
                          flex: "0 0 78vw",   // ~78% of screen width per card
                          maxWidth: 300,
                          scrollSnapAlign: "start",
                        }}
                      >
                        <ProjectCard project={p} onClick={() => handleProjectClick(p)} />
                      </Box>
                    ))}
                  </Box>

                  {/* Dot indicators */}
                  <Stack direction="row" justifyContent="center" spacing={0.75} mt={2}>
                    {projects.map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: i === 0 ? 18 : 6,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: i === 0 ? GOLD : alpha(BROWN, 0.2),
                          transition: "width 0.3s",
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                {/* ── Desktop: original grid ── */}
                <Box
                  sx={{
                    display: { xs: "none", md: "grid" },
                    gridTemplateColumns: { sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    gap: 3,
                  }}
                >
                  {projects.map((p) => (
                    <ProjectCard key={p.id} project={p} onClick={() => handleProjectClick(p)} />
                  ))}
                </Box>
              </>
            )}
          </Container>
        </Box>
      {/* ══ FOOTER CTA ════════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: BROWN, borderTop: `1px solid ${alpha("#fff",0.1)}`, py: { xs: 8, md: 10 }, textAlign: "center" }}>
        <Box sx={{ display: "flex", height: "5px", mb: 7 }}>
          {SDG_BAR.map((c,i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
        </Box>
        <Container maxWidth="sm">
          <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 700, mb: 2, color: WHITE }}>
            Ready to Build Africa's Future?
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: alpha(WHITE,0.72), fontSize: "1rem", lineHeight: 1.8, mb: 4 }}>
            Join us in constructing sustainable homes, clean energy, and empowered communities — one community at a time.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="center" spacing={2}>
            <Button variant="contained" size="large" onClick={() => setVolunteerModalOpen(true)} endIcon={<ArrowForwardIcon />}
              sx={{ bgcolor: WHITE, color: BROWN, fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: "14px", px: 4, py: 1.4, borderRadius: 2, "&:hover":{ bgcolor: OFF_WHITE }, boxShadow: `0 6px 24px ${alpha("#000",0.2)}` }}>
              Volunteer Now
            </Button>
            <Button variant="outlined" size="large" href="mailto:africabuildtoshelter@gmail.com"
              sx={{ borderColor: alpha(WHITE,0.55), color: WHITE, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: "14px", px: 4, py: 1.4, borderRadius: 2, "&:hover":{ borderColor: WHITE, bgcolor: alpha(WHITE,0.1) } }}>
              africabuildtoshelter@gmail.com
            </Button>
          </Stack>
          <Typography sx={{ fontFamily: "'Space Mono', monospace", fontSize: "10px", color: alpha(WHITE,0.3), mt: 6, letterSpacing: "1.5px" }}>
            BUILD TO SHELTER AFRICA · PAN-AFRICAN NGO · EST. 2025 · KAMPALA, UGANDA
          </Typography>
        </Container>
      </Box>

      {/* ══ MODALS ════════════════════════════════════════════════════════════ */}
      <ProjectModal project={selectedProject} open={Boolean(selectedProject)} onClose={() => setSelectedProject(null)} />
      <VolunteerModal open={volunteerModalOpen} onClose={() => setVolunteerModalOpen(false)} />
    </Box>
  );
}
// MARKER_TEST_12345
